/**
 * Route shape and camera maths for the /monitor spotlight. Long legs are
 * drawn as great-circle arcs (like a seatback flight map) and always take
 * the short way round, across the date line when that is shorter; a route
 * from Korea to Florida arcs over the North Pacific instead of crossing
 * Asia, Africa and the Atlantic.
 */

/** [lng, lat], GeoJSON order. Longitudes may run past ±180 to stay continuous. */
export type LngLat = [number, number];

const EARTH_RADIUS_KM = 6371;
/** Legs longer than this are bent along the great circle, in steps this long. */
export const ARC_STEP_KM = 250;
/**
 * Arcs that would swing further from the equator than this look absurd on a
 * flat map (Singapore to Santiago dips towards Antarctica); such legs are
 * drawn straight instead, still the short way round.
 */
const MAX_ARC_LAT = 72;
/** Web Mercator stops here. */
const MAX_MERCATOR_LAT = 85.05;
/** Mapbox world size in pixels at zoom 0. */
const TILE_SIZE = 512;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/** `lng` moved by whole turns to within 180° of `reference`. */
export const wrapLngNear = (lng: number, reference: number): number =>
  lng + 360 * Math.round((reference - lng) / 360);

/** Shortest signed longitude difference, in [-180, 180]. */
export const lngDelta = (from: number, to: number): number =>
  wrapLngNear(to, from) - from;

/**
 * Points along the great circle from `a` to `b`, excluding `a` and ending
 * at `b`. Short legs come back as just `b`.
 */
const greatCircleLeg = (a: LngLat, b: LngLat): LngLat[] => {
  const [lng1, lat1] = [toRad(a[0]), toRad(a[1])];
  const [lng2, lat2] = [toRad(b[0]), toRad(b[1])];
  const angle =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lng2 - lng1) / 2) ** 2
      )
    );
  const steps = Math.ceil((angle * EARTH_RADIUS_KM) / ARC_STEP_KM);
  // Nearly antipodal legs have no single great circle; keep them straight.
  if (steps <= 1 || Math.sin(angle) < 1e-6) return [b];
  const out: LngLat[] = [];
  for (let i = 1; i <= steps; i += 1) {
    const f = i / steps;
    const wa = Math.sin((1 - f) * angle) / Math.sin(angle);
    const wb = Math.sin(f * angle) / Math.sin(angle);
    const x =
      wa * Math.cos(lat1) * Math.cos(lng1) +
      wb * Math.cos(lat2) * Math.cos(lng2);
    const y =
      wa * Math.cos(lat1) * Math.sin(lng1) +
      wb * Math.cos(lat2) * Math.sin(lng2);
    const z = wa * Math.sin(lat1) + wb * Math.sin(lat2);
    out.push([toDeg(Math.atan2(y, x)), toDeg(Math.atan2(z, Math.hypot(x, y)))]);
  }
  if (out.some(([, lat]) => Math.abs(lat) > MAX_ARC_LAT)) return [b];
  out[out.length - 1] = b;
  return out;
};

/**
 * The line through `coords`, long legs bent along great circles, with
 * continuous longitudes. `indexOf[i]` is where input point i landed.
 */
export const shapeLine = (
  coords: LngLat[]
): { line: LngLat[]; indexOf: number[] } => {
  const line: LngLat[] = [];
  const indexOf: number[] = [];
  coords.forEach((point, i) => {
    if (i === 0) {
      line.push(point);
      indexOf.push(0);
      return;
    }
    greatCircleLeg(coords[i - 1], point).forEach(([lng, lat]) => {
      line.push([wrapLngNear(lng, line[line.length - 1][0]), lat]);
    });
    indexOf.push(line.length - 1);
  });
  return { line, indexOf };
};

const isLngLat = ([lng, lat]: LngLat) =>
  Number.isFinite(lng) && Number.isFinite(lat);

/**
 * The spotlight route split at the shipment, as drawn: traveled part up to
 * route point `splitIndex`, remaining part from it. The whole line is moved
 * by whole turns so that the split sits next to `anchorLng` (the shipment),
 * which keeps it on the same world copy as the shipment's ring.
 */
export const shapeMonitorRoute = (
  points: LngLat[],
  splitIndex: number,
  anchorLng: number | null
): { traveled: LngLat[]; remaining: LngLat[] } => {
  const valid = points.filter(isLngLat);
  if (valid.length === 0) return { traveled: [], remaining: [] };
  const split = Math.min(Math.max(0, splitIndex), valid.length - 1);
  const { line, indexOf } = shapeLine(valid);
  const at = indexOf[split];
  const reference = anchorLng ?? valid[split][0];
  const shift = wrapLngNear(line[at][0], reference) - line[at][0];
  const shifted = line.map(([lng, lat]): LngLat => [lng + shift, lat]);
  return {
    traveled: shifted.slice(0, at + 1),
    remaining: shifted.slice(at),
  };
};

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const toPadding = (
  padding: number | Partial<Padding> | null | undefined
): Padding => {
  if (typeof padding === 'number')
    return { top: padding, right: padding, bottom: padding, left: padding };
  return {
    top: padding?.top ?? 0,
    right: padding?.right ?? 0,
    bottom: padding?.bottom ?? 0,
    left: padding?.left ?? 0,
  };
};

const mercatorX = (lng: number) => (lng + 180) / 360;
const mercatorY = (lat: number) => {
  const clamped = Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
  return (
    (1 - Math.log(Math.tan(Math.PI / 4 + toRad(clamped) / 2)) / Math.PI) / 2
  );
};

/**
 * The zoom at which `coords` just fit inside the viewport minus `padding`
 * (north-up Mercator, as the monitor map is). Null when nothing to fit or
 * no room is left. Longitudes may run past ±180.
 */
export const zoomToFit = (
  coords: LngLat[],
  viewport: { width: number; height: number },
  padding: Padding
): number | null => {
  const valid = coords.filter(isLngLat);
  if (valid.length === 0) return null;
  const xs = valid.map(([lng]) => mercatorX(lng));
  const ys = valid.map(([, lat]) => mercatorY(lat));
  const spanX = Math.max(...xs) - Math.min(...xs);
  const spanY = Math.max(...ys) - Math.min(...ys);
  const width = viewport.width - padding.left - padding.right;
  const height = viewport.height - padding.top - padding.bottom;
  if (width <= 0 || height <= 0) return null;
  const zoomX = spanX > 0 ? Math.log2(width / (spanX * TILE_SIZE)) : Infinity;
  const zoomY = spanY > 0 ? Math.log2(height / (spanY * TILE_SIZE)) : Infinity;
  const zoom = Math.min(zoomX, zoomY);
  return Number.isFinite(zoom) ? zoom : null;
};

/**
 * The route's GeoJSON features, travelled then remaining, leaving out any
 * part with fewer than two points: a GeoJSON LineString needs at least two
 * (before pickup, or at the very start, the travelled part is one point).
 */
export const routeLineFeatures = (
  traveled: (number | string)[][],
  remaining: (number | string)[][]
) =>
  (
    [
      ['traveled', traveled],
      ['remaining', remaining],
    ] as const
  )
    .filter(([, coordinates]) => coordinates.length >= 2)
    .map(([segment, coordinates]) => ({
      type: 'Feature' as const,
      properties: { segment },
      geometry: { type: 'LineString' as const, coordinates },
    }));
